// «Primeros pasos»: guía para una clínica recién dada de alta. Cada paso se marca solo cuando está hecho.
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { collection, doc, onSnapshot, query, setDoc, where } from '../fs';
import { db } from '../firebase';
import { CLINICAS } from '../clinicas';
import { infoOrg, orgActual } from '../org';
import { usePanel } from '../App.jsx';

const conHorario = (c) => !!(c.bot?.horario || Object.values(c.bot?.tramos || {}).some((l) => l?.length));

// Estado de cada paso (en vivo)
export function usePasos(activo = true) {
  const [wa, setWa] = useState({});
  const [equipo, setEquipo] = useState(0);
  const ids = CLINICAS.map((c) => c.id).join(',');
  useEffect(() => {
    if (!activo || !ids) return undefined;
    const fin = ids.split(',').map((id) => onSnapshot(doc(db, 'bot', id), (s) => {
      const x = s.data() || {};
      setWa((w) => ({ ...w, [id]: !!(x.phoneNumberId || (x.proveedor === 'ycloud' && x.ycloudClave)) }));
    }, () => {}));
    return () => fin.forEach((f) => f());
  }, [activo, ids]);
  useEffect(() => {
    if (!activo) return undefined;
    let u = 0, i = 0;
    const a = onSnapshot(query(collection(db, 'usuarios'), where('org', '==', orgActual())), (s) => { u = s.size; setEquipo(u - 1 + i); }, () => {});
    const b = onSnapshot(collection(db, 'invitaciones'), (s) => { i = s.size; setEquipo(Math.max(0, u - 1) + i); }, () => {});
    return () => { a(); b(); };
  }, [activo]);
  const o = infoOrg();
  const pasos = [
    { k: 'datos', hecho: CLINICAS.some(conHorario), titulo: 'Horario y datos de la clínica', texto: 'Horario, servicios, urgencias y precios orientativos. Es lo que el asistente de WhatsApp contesta a tus clientes.', ir: '/mi-clinica?tab=clinicas', boton: 'Rellenar datos', min: '5 min' },
    { k: 'whatsapp', pronto: !WA_LISTO && !Object.values(wa).some(Boolean), hecho: Object.values(wa).some(Boolean), titulo: 'Conectar el WhatsApp de la clínica', texto: 'Entras con Facebook, eliges el número y escaneas un QR con el móvil. El WhatsApp Business del móvil sigue funcionando igual.', ir: '/whatsapp?tab=ajustes', boton: 'Conectar WhatsApp', min: '5 min' },
    { k: 'facturas', hecho: Object.keys(o.correoFacturas || o.ingesta || {}).length > 0, titulo: 'Recibir las facturas de proveedores', texto: 'Creamos una dirección de correo para tu clínica. Pon un reenvío automático y las facturas entrarán solas, leídas y ordenadas.', ir: '/mi-clinica?tab=conexiones', boton: 'Crear dirección', min: '3 min' },
    { k: 'imagen', hecho: !!(o.logoUrl || o.colorMenu || o.color), titulo: 'Tu logo y tus colores', texto: 'Sube el logo de la clínica y elige el color del menú para que el panel sea vuestro.', ir: '/mi-clinica?tab=org', boton: 'Personalizar', min: '1 min' },
    { k: 'equipo', hecho: equipo > 0, titulo: 'Invitar a tu equipo', texto: 'Manda un enlace a cada persona. Tú decides qué ve cada uno: recepción, veterinarios, auxiliares…', ir: '/accesos', boton: 'Invitar', min: '2 min' },
  ];
  // AUD-ONB-02: los pasos que aún no se pueden hacer («pronto») no cuentan en el total
  const posibles = pasos.filter((p) => !p.pronto || p.hecho);
  return { pasos, hechos: posibles.filter((p) => p.hecho).length, total: posibles.length };
}

// Mientras Meta/YCloud no estén listos, el paso de WhatsApp sale como «pronto» (poner VITE_WA_LISTO=1 en .env al activarlo)
const WA_LISTO = !!import.meta.env.VITE_WA_LISTO;

export default function PrimerosPasos() {
  const { user } = usePanel();
  const { pasos, hechos, total } = usePasos();
  const siguiente = pasos.find((p) => !p.hecho && !p.pronto);
  const ocultar = () => setDoc(doc(db, 'preferencias', user.uid), { primerosOculto: true }, { merge: true }).catch(() => {});
  return (
    <section className="pp">
      <div className="pp-cab">
        <div>
          <p className="pp-eyebrow">Bienvenido a {infoOrg().nombre || 'tu panel'}</p>
          <h1>Primeros pasos</h1>
          <p className="subtitulo">Cinco pasos para dejar tu clínica lista. Se marcan solos cuando están hechos.</p>
        </div>
        <div className="pp-progreso" aria-label={`${hechos} de ${total} hechos`}>
          <b>{hechos}<span>/{total}</span></b>
          <div className="pp-barra"><i style={{ width: `${(hechos / total) * 100}%` }} /></div>
        </div>
      </div>
      {hechos < total && (
        <NavLink to="/configurar" className="pp-asistente">
          <span><b>Hazlo en 5 minutos con el asistente</b>Una pregunta por pantalla, sin buscar nada.</span>
          <em>Empezar →</em>
        </NavLink>
      )}
      <ol className="pp-lista">
        {pasos.map((p, n) => (
          <li key={p.k} className={`pp-paso${p.hecho ? ' hecho' : ''}${p === siguiente ? ' siguiente' : ''}`}>
            <span className="pp-num" aria-hidden="true">{p.hecho ? '✓' : n + 1}</span>
            <div className="pp-txt">
              <b>{p.titulo}</b>
              <p>{p.texto}</p>
            </div>
            <div className="pp-acc">
              {p.pronto ? <span className="pp-pronto">Disponible en unos días</span> : <>
                {p.hecho ? <span className="pp-ok">Hecho</span> : <small>{p.min}</small>}
                <NavLink className={p === siguiente ? 'btn' : 'btn sec'} to={p.ir}>{p.hecho ? 'Revisar' : p.boton}</NavLink>
              </>}
            </div>
          </li>
        ))}
      </ol>
      <div className="pp-pie">
        {hechos === total
          ? <p><b>¡Todo listo!</b> Tu clínica ya está funcionando con {document.title || 'el panel'}.</p>
          : <p className="tenue">¿Dudas? Escríbenos a <a href="mailto:info@vetpanel.es">info@vetpanel.es</a> y te ayudamos a dejarlo listo.</p>}
        <button className="entrada-enlace" onClick={ocultar}>Ocultar esta guía del menú</button>
      </div>
    </section>
  );
}
